import { EditOutlined, PlusOutlined, SyncOutlined, UserOutlined } from "@ant-design/icons";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Button, Input, Layout, Menu, PageHeader, Popconfirm, Row, Space, Table, Tag, Tooltip } from "antd";
import { useApplicationContext } from "components/Application";
import React, { Fragment, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";

const accountUsersQuery = gql`
  query ($pagination: ConnectionPagination, $sorting: AccountUserSorting, $searchQuery: String) {
    accountUsers(pagination: $pagination, sorting: $sorting, searchQuery: $searchQuery) {
      nodes {
        id
        code
        email
        role
        companyName
      }
      pageInfo {
        hasNextPage
        hasPreviousPage
        page
        size
      }
      totalCount
      totalPages
    }
  }
`;

const startImpersonatingAccountUserMutation = gql`
  mutation ($input: StartImpersonatingAccountUserInput!) {
    startImpersonatingAccountUser(input: $input) {
      successful
    }
  }
`;

const magoSyncUsersMutation = gql`
  mutation {
    magoSyncUsers {
      successful
    }
  }
`;

export const List: React.FC<{}> = () => {
  const { session, refetchSession } = useApplicationContext();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState<string>("");

  const variables = {
    pagination: { page: 1, pageSize: 0 },
    orderBy: { direction: "ASC", field: "ID" },
    searchQuery,
  };
  const { loading, data } = useQuery(accountUsersQuery, { variables, fetchPolicy: "cache-and-network" });

  const [execMutation] = useMutation(startImpersonatingAccountUserMutation, {
    errorPolicy: "none",
    onCompleted: () => {
      refetchSession();
      navigate("/");
    },
  });

  const handleImpersonateClick = (id: string) => {
    execMutation({ variables: { input: { id } } });
  };

  const handleEditClick = (id: string) => {
    navigate(`${id}/edit`);
  };

  const handleSearch = (value: string) => setSearchQuery(value);
  const handleSearchCancel = () => setSearchQuery("");
  const handleTableChange = () => {};

  const [magoSyncUsers, { loading: syncLoading }] = useMutation(magoSyncUsersMutation, {});
  const [syncEnabled, setSyncEnabled] = useState(true);

  const columns = [
    {
      title: "Code",
      dataIndex: "code",
      render: (value: string, resource: any) => <NavLink to={resource.id}>{value}</NavLink>,
      sorter: (a: any, b: any) => a.code.localeCompare(b.code),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "users.email",
      responsive: ["sm"],
      render: (value: string, resource: any) => (
        <NavLink to={resource.id}>
          {value === session.currentUser.email ? <Tag>ME</Tag> : ""}
          {value}
        </NavLink>
      ),
      sorter: (a: any, b: any) => a.email.localeCompare(b.email),
    },
    {
      title: "Company Name",
      dataIndex: "companyName",
      key: "users.companyName",
      render: (value: string) => <span>{value}</span>,
      responsive: ["lg"],
      sorter: (a: any, b: any) => a.companyName?.localeCompare(b.companyName),
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "users.role",
      responsive: ["sm"],
      render: (value: string) => (value === "USER" ? <Tag>{value}</Tag> : <Tag color="blue">{value}</Tag>),
      sorter: (a: any, b: any) => a.role.localeCompare(b.role),
      filters: [
        {
          text: 'USER',
          value: 'USER',
        },
        {
          text: 'ADMIN',
          value: 'ADMIN',
        },
        {
          text: 'SALESPERSON',
          value: 'SALESPERSON',
        },
      ],
      onFilter: (value: boolean, record: any) => record.role.indexOf(value) === 0,
    },
    {
      title: "Actions",
      dataIndex: "id",
      render: (id: string, resource: any) => (
        <Space>
          <Tooltip title="Edit">
            <Button onClick={() => handleEditClick(id)} icon={<EditOutlined />}></Button>
          </Tooltip>
          {resource.role === "USER" && (
            <Tooltip title="Impersonate">
              <Button danger onClick={() => handleImpersonateClick(id)} icon={<UserOutlined />}></Button>
            </Tooltip>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Fragment>
      <Layout.Header className="header-background">
        <Row justify="space-between">
          <Menu theme="light" mode="horizontal">
            <Input.Search
              placeholder="search"
              onSearch={handleSearch}
              key={variables.searchQuery ?? "__search__"}
              defaultValue={variables.searchQuery ?? ""}
              style={{ width: 500, margin: "16px 30px" }}
              />
            <Button disabled={searchQuery.length === 0} onClick={handleSearchCancel} style={{ margin: "16px 0" }}>
              Cancel
            </Button>
          </Menu>
        </Row>
      </Layout.Header>
      <Layout className="content">
        <PageHeader
          ghost={true}
          title="Users List"
          extra={
            <Space size="large">
              <Popconfirm
                disabled={!syncEnabled}
                title="Are you sure?"
                icon={null}
                onConfirm={() => {
                  magoSyncUsers();
                  setSyncEnabled(false);
                }}
              >
                <Button loading={syncLoading} disabled={!syncEnabled}>
                  <SyncOutlined />
                  Mago Sync
                </Button>
              </Popconfirm>
              <Button onClick={() => navigate("new")} type="primary">
                <PlusOutlined />
                New
              </Button>
            </Space>
          }
        />
        <Layout.Content className="content">
          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={data?.accountUsers.nodes}
            pagination={{
              hideOnSinglePage: true,
              showSizeChanger: true,
              showTotal: (total, range) => `Showing ${range} of ${total}`,
              // current: data?.pageInfo?.page ?? 1,
              // pageSize: data?.pageInfo?.size ?? 10,
              total: data?.totalCount ?? 0,
            }}
            onChange={handleTableChange}
          />
        </Layout.Content>
      </Layout>
    </Fragment>
  );
};
